import { AlertTriangle } from 'lucide-react';
import React, { useState, useCallback, useMemo, memo } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type {
  IOneItemInPrepareForRequest,
  ITodayOffersForRequest,
} from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';

import CompetitorsDialog from './CompetitorsDialog';
import FinalStage from './FinalStage';
import ItemsDialog from './ItemsRequestDialog';
import ItemsForRequestTable from './itemsTable';
import { OfferHeader } from './OfferHeader';
import PurchaseHistoryDialog from './PurchaseHistoryDialog';
import UnderRequestDialog from './UnderRequestsDialog';

interface PrepareItemsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  items: IOneItemInPrepareForRequest[];
  client: ITodayOffersForRequest;
  handleUpdateOneItemLink: (
    item: IOneItemInPrepareForRequest,
    skipResetPage?: boolean,
  ) => void;
  handleLoadingStage: (
    value: boolean,
    type: 'check' | 'upload' | 'update' | 'main',
  ) => void;
  isLoading: boolean;
  closeDialogFinalFn: () => void;
  handleResetStage: () => void;
  setItems: React.Dispatch<React.SetStateAction<IOneItemInPrepareForRequest[]>>;
  handleAddOneItemLink: (item: IOneItemInPrepareForRequest) => void;
}

// Memoized confirmation dialog component
const ConfirmationDialog = memo(
  ({
    isOpen,
    onClose,
    onConfirm,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
  }) => (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hiddenCloseIcon size="sm" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            تأكيد الإلغاء
          </DialogTitle>
          <DialogDescription className="pt-2 text-center">
            هل أنت متأكد من رغبتك في الإلغاء؟ سيتم فقدان كافة التعديلات التي قمت
            بإجرائها.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-between sm:justify-between">
          <Button variant="outline" onClick={onClose}>
            استمرار التحرير
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            إلغاء ومسح التعديلات
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ),
);
ConfirmationDialog.displayName = 'ConfirmationDialog';

// Main dialog content component (memoized)
const MainDialogContent = memo(
  ({
    client,
    items,
    handleReset,
    handleAddOneItemLink,
    handleLoadingStage,
    isLoading,
    handleFinishStage,
    setItems,
    handleShowCompetitors,
    handleSelectItem,
    handleShowItemGroup,
    handleShowUnderRequest,
    handleShowPurchaseHistory,
    handleUpdateOneItemLink,
  }: {
    client: ITodayOffersForRequest;
    items: IOneItemInPrepareForRequest[];
    handleReset: () => void;
    handleAddOneItemLink: (item: IOneItemInPrepareForRequest) => void;
    handleLoadingStage: (
      value: boolean,
      type: 'check' | 'upload' | 'update' | 'main',
    ) => void;
    isLoading: boolean;
    handleFinishStage: (value: boolean) => void;
    setItems: React.Dispatch<
      React.SetStateAction<IOneItemInPrepareForRequest[]>
    >;
    handleShowCompetitors: (value: boolean) => void;
    handleSelectItem: (item: IOneItemInPrepareForRequest) => void;
    handleShowItemGroup: (value: boolean) => void;
    handleShowUnderRequest: (value: boolean) => void;
    handleShowPurchaseHistory: (value: boolean) => void;
    handleUpdateOneItemLink: (item: IOneItemInPrepareForRequest) => void;
  }) => (
    <>
      <DialogHeader>
        <DialogTitle className="hidden">
          طلبية شراء للعميل {client?.ClientName}
        </DialogTitle>
        <DialogDescription className="hidden">
          تجهيز طلبية شراء للعميل {client?.ClientName} مع {items.length} عنصر
        </DialogDescription>
        <OfferHeader
          client={client}
          handleResetStage={handleReset}
          items={items}
          handleAddOneItemLink={handleAddOneItemLink}
          handleLoadingStage={handleLoadingStage}
          isLoading={isLoading}
          handleFinishStage={handleFinishStage}
        />
      </DialogHeader>
      <div className="overflow-auto p-4">
        <ItemsForRequestTable
          items={items}
          setItems={setItems}
          handleShowCompetitors={handleShowCompetitors}
          handleSelectItem={handleSelectItem}
          handleShowItemGroup={handleShowItemGroup}
          handleShowUnderRequest={handleShowUnderRequest}
          handleShowPurchaseHistory={handleShowPurchaseHistory}
          handleUpdateOneItemLink={handleUpdateOneItemLink}
          handleLoadingStage={handleLoadingStage}
          isLoading={isLoading}
        />
      </div>
    </>
  ),
);
MainDialogContent.displayName = 'MainDialogContent';

// Add this component before the main PrepareRequest component
interface DialogsContainerProps {
  showConfirmation: boolean;
  handleCancelClose: () => void;
  handleConfirmClose: () => void;
  selectedItem: IOneItemInPrepareForRequest | null;
  showCompetitors: boolean;
  handleShowCompetitors: (show: boolean) => void;
  showItemGroup: boolean;
  handleShowItemGroup: (show: boolean) => void;
  showUnderRequest: boolean;
  handleShowUnderRequest: (show: boolean) => void;
  showPurchaseHistory: boolean;
  handleShowPurchaseHistory: (show: boolean) => void;
  finishStage: boolean;
  handleFinishStage: (show: boolean) => void;
  client: ITodayOffersForRequest;
  items: IOneItemInPrepareForRequest[];
  closeDialogFinalFn: () => void;
  handleLoadingStage: (
    value: boolean,
    type: 'check' | 'upload' | 'update' | 'main',
  ) => void;
  isLoading: boolean;
}

const DialogsContainer = React.memo(
  ({
    showConfirmation,
    handleCancelClose,
    handleConfirmClose,
    selectedItem,
    showCompetitors,
    handleShowCompetitors,
    showItemGroup,
    handleShowItemGroup,
    showUnderRequest,
    handleShowUnderRequest,
    showPurchaseHistory,
    handleShowPurchaseHistory,
    finishStage,
    handleFinishStage,
    client,
    items,
    closeDialogFinalFn,
    handleLoadingStage,
    isLoading,
  }: DialogsContainerProps) => {
    return (
      <>
        {showConfirmation && (
          <ConfirmationDialog
            isOpen={showConfirmation}
            onClose={handleCancelClose}
            onConfirm={handleConfirmClose}
          />
        )}

        {selectedItem && showCompetitors && (
          <CompetitorsDialog
            item={selectedItem}
            isOpen={showCompetitors}
            closeFn={() => handleShowCompetitors(false)}
          />
        )}

        {selectedItem && showItemGroup && (
          <ItemsDialog
            item={selectedItem}
            isOpen={showItemGroup}
            closeFn={() => handleShowItemGroup(false)}
          />
        )}

        {selectedItem && showUnderRequest && (
          <UnderRequestDialog
            item={selectedItem}
            isOpen={showUnderRequest}
            closeFn={() => handleShowUnderRequest(false)}
          />
        )}

        {selectedItem && showPurchaseHistory && (
          <PurchaseHistoryDialog
            item={selectedItem}
            isOpen={showPurchaseHistory}
            closeFn={() => handleShowPurchaseHistory(false)}
          />
        )}

        {finishStage && (
          <FinalStage
            client={client}
            items={items}
            isOpen={finishStage}
            closeFn={() => handleFinishStage(false)}
            closeDialogFinalFn={closeDialogFinalFn}
            handleLoadingStage={handleLoadingStage}
            isLoading={isLoading}
          />
        )}
      </>
    );
  },
);

export const PrepareItemsDialog = memo(function PrepareItemsDialog({
  isOpen,
  onClose,
  items,
  client,
  handleUpdateOneItemLink,
  handleLoadingStage,
  isLoading,
  closeDialogFinalFn,
  handleResetStage,
  handleAddOneItemLink,
  setItems,
}: PrepareItemsDialogProps) {
  // State management
  const [showUnderRequest, setShowUnderRequest] = useState(false);
  const [showCompetitors, setShowCompetitors] = useState(false);
  const [showItemGroup, setShowItemGroup] = useState(false);
  const [finishStage, setFinishStage] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<IOneItemInPrepareForRequest | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);

  // Memoized derived values
  const hasModifiedItems = useMemo(
    () => items.some((item) => item.Qty > 0),
    [items],
  );

  // Memoized callbacks
  const handleShowUnderRequest = useCallback((value: boolean) => {
    setShowUnderRequest(value);
  }, []);

  const handleSelectItem = useCallback((item: IOneItemInPrepareForRequest) => {
    setSelectedItem(item);
  }, []);

  const handleShowCompetitors = useCallback((value: boolean) => {
    setShowCompetitors(value);
  }, []);

  const handleShowItemGroup = useCallback((value: boolean) => {
    setShowItemGroup(value);
  }, []);

  const handleShowPurchaseHistory = useCallback((value: boolean) => {
    setShowPurchaseHistory(value);
  }, []);

  const handleFinishStage = useCallback((value: boolean) => {
    setFinishStage(value);
  }, []);

  // Confirmation dialog handlers
  const handleCloseAttempt = useCallback(() => {
    if (hasModifiedItems) {
      setShowConfirmation(true);
    } else {
      onClose();
    }
  }, [hasModifiedItems, onClose]);

  const handleReset = useCallback(() => {
    if (hasModifiedItems) {
      setShowConfirmation(true);
    } else {
      handleResetStage();
    }
  }, [hasModifiedItems, handleResetStage]);

  const handleDialogClose = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCloseAttempt();
      }
    },
    [handleCloseAttempt],
  );

  const handleConfirmClose = useCallback(() => {
    setShowConfirmation(false);
    handleResetStage();
  }, [handleResetStage]);

  const handleCancelClose = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  // Prevent default Escape key handler
  const handleEscapeKey = useCallback(
    (e: KeyboardEvent) => {
      e.preventDefault();
      handleCloseAttempt();
    },
    [handleCloseAttempt],
  );

  // Add this memoized dialogs container
  const memoizedDialogs = useMemo(
    () => (
      <DialogsContainer
        showConfirmation={showConfirmation}
        handleCancelClose={handleCancelClose}
        handleConfirmClose={handleConfirmClose}
        selectedItem={selectedItem}
        showCompetitors={showCompetitors}
        handleShowCompetitors={handleShowCompetitors}
        showItemGroup={showItemGroup}
        handleShowItemGroup={handleShowItemGroup}
        showUnderRequest={showUnderRequest}
        handleShowUnderRequest={handleShowUnderRequest}
        showPurchaseHistory={showPurchaseHistory}
        handleShowPurchaseHistory={handleShowPurchaseHistory}
        finishStage={finishStage}
        handleFinishStage={handleFinishStage}
        client={client}
        items={items}
        closeDialogFinalFn={closeDialogFinalFn}
        handleLoadingStage={handleLoadingStage}
        isLoading={isLoading}
      />
    ),
    [
      showConfirmation,
      handleCancelClose,
      handleConfirmClose,
      selectedItem,
      showCompetitors,
      handleShowCompetitors,
      showItemGroup,
      handleShowItemGroup,
      showUnderRequest,
      handleShowUnderRequest,
      showPurchaseHistory,
      handleShowPurchaseHistory,
      finishStage,
      handleFinishStage,
      client,
      items,
      closeDialogFinalFn,
      handleLoadingStage,
      isLoading,
    ],
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent
        hiddenCloseIcon
        className="overflow-hidden p-0"
        size="full"
        overlayClass="custom-dialog-overlay"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={handleEscapeKey}
        aria-describedby="prepare-request-dialog-description"
      >
        <MainDialogContent
          client={client}
          items={items}
          handleReset={handleReset}
          handleAddOneItemLink={handleAddOneItemLink}
          handleLoadingStage={handleLoadingStage}
          isLoading={isLoading}
          handleFinishStage={handleFinishStage}
          setItems={setItems}
          handleShowCompetitors={handleShowCompetitors}
          handleSelectItem={handleSelectItem}
          handleShowItemGroup={handleShowItemGroup}
          handleShowUnderRequest={handleShowUnderRequest}
          handleShowPurchaseHistory={handleShowPurchaseHistory}
          handleUpdateOneItemLink={handleUpdateOneItemLink}
        />
        {memoizedDialogs}
      </DialogContent>
    </Dialog>
  );
});
